import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, getCategoryBySlug, getRelatedComics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">漫画不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = getCategoryBySlug(comic.category);
  const relatedComics = getRelatedComics(comic.id, comic.category);

  const chapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(new Date(comic.updatedAt).getTime() - i * 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
  }));

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 好看的韩国漫画在线免费阅读`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2026-01-01',
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: { '@type': 'Organization', name: '韩漫画网' },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <article className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '漫画', href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <div className="mt-6 flex flex-col lg:flex-row gap-8">
            <div className="w-full lg:w-[300px] shrink-0">
              <div className="rounded-lg overflow-hidden bg-background-200 aspect-[3/4] sticky top-24">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 韩国漫画封面`}
                  title={`${comic.title} - 好看的韩国漫画在线免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">作者：{comic.author}</p>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span
                  className={`text-xs px-3 py-1 rounded-full font-medium ${
                    comic.status === '连载中'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-secondary-500 text-background-50'
                  }`}
                >
                  {comic.status}
                </span>
                <span className="flex items-center gap-1 text-sm text-accent-600">
                  <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
                  {comic.rating} 分
                </span>
                <span className="text-sm text-foreground-500">共 {comic.chapters} 话</span>
                <span className="text-sm text-foreground-500">
                  {new Intl.NumberFormat('zh-CN').format(comic.views)} 次阅读
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mt-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 p-5 rounded-lg bg-background-100">
                <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading text-base font-semibold text-foreground-900">章节列表</h2>
                  {comic.chapters > 20 && (
                    <span className="text-xs text-foreground-500">显示最近20话，共{comic.chapters}话</span>
                  )}
                </div>
                <div className="border border-background-200/70 rounded-lg divide-y divide-background-200/70">
                  {chapters.map((ch) => (
                    <div
                      key={ch.num}
                      className="flex items-center justify-between px-4 py-3 hover:bg-background-50 transition-colors cursor-pointer"
                    >
                      <span className="text-sm text-foreground-800">{ch.title}</span>
                      <span className="text-xs text-foreground-400">{ch.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <section className="mt-12 md:mt-16">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop>
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}